// @flow
import Ingest from '@cchome/ingest-react';
import analyticsUtil, {getInstallWorkflowName} from '../utils/AnalyticsUtil';
import ingestUtil from '../utils/IngestUtil';
import appsUtil from '../utils/AppsUtil';
import DeviceDetectionUtil from '../utils/DeviceDetectionUtil';
import {ACTION_BAR_SECTION, ACTION_ID} from '@ccxi/ccxi-apps/src/utils/modelMapper/appModelMapper/appModelConstants';
import {actionBarSubtype, actionIds, buttonData, subHeadingFilter} from '../constants/appsConstants';
import {thorAppsStatesCategory} from '../constants/wamConstants';
import {ANALYTICS} from '@ccxi/ccxi-apps/src/utils/enums';
import CompatibilityUtil from '../utils/CompatibilityUtil';
import {setExpCampaignAndVariation, setExpCampaignAndVariationFromSophia} from '../utils/TestUtil';

let logAnalytics = 0;
let logMetricsCollection = 0;
const constants = {
	rsidDev: 'adbem-cchome-dev',
	rsidProd: 'adbem-cchome-prod'
};
const contentConstants = {
	trialAvailable: 'Trial Available',
	trialInstalled: 'Trial Installed',
	entitledAvailable: 'Entitled Available',
	entitledInstalled: 'Entitled Installed',
	entitled: 'entitled',
};

export default class CCWebAnalytics {
	ingest: Object;

	constructor(env: string) {
		const initObj = this.getInitObject(env);

		this.ingest = new Ingest(initObj);

		logMetricsCollection = 0;
		logAnalytics = 0;

		if (window.logAppsMetrics && window.logAppsMetrics > 0) {
			logMetricsCollection = 1;
		}
		if (window.logAnalytics && window.logAnalytics > 0) {
			logAnalytics = 1;
		}
	}

	getContentType(stateCategory: string, sapCode: string) {
		const productConfig = appsUtil.getFFCProductConfigBySapCode(sapCode);
		let isEntitled = false;
		if (productConfig && appsUtil.isAppEntitledNew(productConfig, productConfig.id, productConfig.type) && !appsUtil.disableProduct(productConfig.id)) {
			isEntitled = true;
		}
		switch (stateCategory) {
			case subHeadingFilter.trailApps:
				return contentConstants.trialAvailable;
			case subHeadingFilter.subscribed:
			case contentConstants.entitled:
				return contentConstants.entitledAvailable;
			case subHeadingFilter.installed:
			case thorAppsStatesCategory.availableToUpdate:
			case thorAppsStatesCategory.installed:
				return isEntitled ? contentConstants.entitledInstalled : contentConstants.trialInstalled;
			case thorAppsStatesCategory.availableToInstall:
			case thorAppsStatesCategory.activeSessions:
			case thorAppsStatesCategory.incompatible:
			case thorAppsStatesCategory.incompatibleWithCheckprevious:
				return isEntitled ? contentConstants.entitledAvailable : contentConstants.trialAvailable;
			default:
				return stateCategory;
		}
	}

	getInitObject(env: string) {
		const initObj = analyticsUtil.initAnalytics();
		if (env) {
			initObj.environment = env;
		}
		initObj.rsid = constants.rsidDev;
		if (initObj.environment === 'production' || initObj.environment === 'prod') {
			initObj.rsid = constants.rsidProd;
		}
		return initObj;
	}

	/**
	 * [initAnalytics Init Ingest]
	 * @return {[none]}
	 */
	initAnalytics() {
		ingestUtil.initAnalytics(this.ingest);
	}

	/**
	 * [logAnalytics A utility method to set multiple values at once]
	 * @param  {[object of key/value pairs]} dataObject [events]
	 * @return {[none]}
	 */
	logAnalytics(dataObject: Object) {
		ingestUtil.setIngestDataValue(this.ingest, dataObject);

		this.ingest.addToCollection();
	}

	/**
	 *
	 * @param {eventObject} genericAnalyticEvent
	 */
	// genericAnalyticEvent(appConfig:Object,eventType:string,eventSubtype:string,viewType:string,expCampaignID?:string,expVariationID?:string, eventValue?:string) {
	genericAnalyticEvent(config: Object) {
		const {componentParams} = config;
		config.analyticObjectList.forEach((eventObject) => {
			const dataObject = {};

			if (eventObject.event) {
				dataObject.setEventWorkflow = eventObject.event.workflow;
				dataObject.setEventCategory = eventObject.event.category;
				dataObject.setEventSubcategory = eventObject.event.subcategory;
				dataObject.setEventType = eventObject.event.type;
				dataObject.setEventSubtype = eventObject.event.subtype;
				dataObject.setEventLanguage = eventObject.event.language;
				dataObject.setEventErrorCode = eventObject.event.error_code;
				dataObject.setEventErrorDesc = eventObject.event.error_desc;
				dataObject.setEventCount = eventObject.event.count;
				dataObject.setEventValue = eventObject.event.value;
				dataObject.setEventGUID = eventObject.event.guid;
			}

			// remove ccxi Debug analytics
			if (dataObject.setEventWorkflow === 'Debug') {
				return;
			}

			if (eventObject.content) {
				dataObject.setContentID = eventObject.content.id;
				dataObject.setContentName = eventObject.content.name;
				dataObject.setContentType = eventObject.content.type;
				dataObject.setContentExtension = eventObject.content.extension;
				dataObject.setContentCategory = eventObject.content.category;
				dataObject.setContentAction = eventObject.content.action;
				dataObject.setContentVersion = eventObject.content.version;
				dataObject.setContentSize = eventObject.content.size;
			}

			if (!analyticsUtil.isSplitEvent(dataObject)) {
				setExpCampaignAndVariation(dataObject);
			}
			else if (eventObject.exp) {
				dataObject.setExpCampaignID = eventObject.exp.campaign_id;
				dataObject.setExpVariationID = eventObject.exp.variation_id;
			}

			if (eventObject.ui) {
				dataObject.setUIViewType = eventObject.ui.view_type;
				dataObject.setUIFilter = eventObject.ui.filter;
				dataObject.setUISearchKeyword = eventObject.ui.searchKeyword;
			}

			dataObject.setEnvironmentServiceName = 'Direct';
			dataObject.setEnvComName = 'CCH';
			dataObject.setEventCategory = 'WEB';

			const sequenceVal = getInstallWorkflowName();
			const hasThor = sequenceVal !== 'kaizen';
			dataObject.setUISequence = sequenceVal;

			const sapCode = dataObject.setContentID;

			// Overide version with supported product version for desktop products
			if (componentParams && componentParams.source === ANALYTICS.PDP && dataObject.setContentID) {
				if (dataObject.setContentCategory === 'desktop'
					&& !appsUtil.isMobileOrTab()) {
					dataObject.setEventContextGUID = appsUtil.getContextGuid(sapCode, hasThor);
					if (!CompatibilityUtil.isAppSupported(sapCode)) {
						dataObject.setContentVersion = CompatibilityUtil.isAnyOlderVersionSupported(sapCode)
							? appsUtil.appsFFCProductList[sapCode].olderSupportedVersion
							: null;
					}
				}
			}

			// Set dataObject.setContentAction for launch CTAs.
			if (componentParams && componentParams.actionId === actionIds.launch) {
				dataObject.setContentAction = appsUtil.goURL(`${buttonData.launch.linkURL}${appsUtil.getProductIdFromSapcode(sapCode)}`);;
			}

			if (componentParams && (componentParams.actionId === ACTION_ID.BUY || componentParams.actionId === ACTION_ID.BUY_NOW)) {
				dataObject.setContentAction = appsUtil.goURL(`${buttonData.buy.linkURL}${sapCode}`);
			}

			if (componentParams && componentParams.actionId === actionIds.launchMobile) {
				if (sapCode && appsUtil.appsFFCMobileList[sapCode]) {
					dataObject.setContentAction = appsUtil.appsFFCMobileList[sapCode].mobileAppUrl;
				}
			}

			// Send url with ingest params in content.action.
			if (dataObject.setContentAction) {
				const guid = dataObject.setEventGUID;
				const url = dataObject.setContentAction;
				const connector = url.indexOf('?') > -1 ? '&' : '?';
				const analyticParam = `${connector}x-product=CCHome&x-product-location=Apps&guid=${guid}`;
				dataObject.setContentAction = `${url}${analyticParam}`;
				if (url.indexOf('stock') > -1) {
					let source = componentParams && componentParams.source;
					switch (dataObject.setEventSubtype) {
						case 'resources-stock':
							source = 'resources';
							break;
						case 'banner':
							source = 'banner';
							break;
					}
					dataObject.setContentAction = appsUtil.addParamsToStockUrl(dataObject.setContentAction, source);
				}
			}

			// Override: event.subtype when sectionId = ACTION_BAR_SECTION.
			// Use actionId - subtype mapping to override this field in case
			// of clicks on CTAs (This cta is present both in pdp header and
			// app tile footer).
			if (componentParams && componentParams.sectionId === ACTION_BAR_SECTION) {
				const subtype = actionBarSubtype[componentParams.actionId];
				if (subtype) {
					dataObject.setEventSubtype = subtype;
				}
			}

			// overiding subtype with its mapping for app-catalog
			if (componentParams && componentParams.source === ANALYTICS.APPS && actionBarSubtype[dataObject.setEventSubtype]) {
				dataObject.setEventSubtype = actionBarSubtype[dataObject.setEventSubtype];
			}

			// setting load time
			if (componentParams && componentParams.shouldSendLoadTime && appsUtil.pageStartTime) {
				const presentTime = new Date().getTime();
				dataObject.setEventCount = presentTime - appsUtil.pageStartTime;
			}

			// Overiding event.subcategory with its mapping
			if (dataObject.setEventSubcategory) {
				if (ANALYTICS.LEFT_NAV[dataObject.setEventSubcategory]) {
					dataObject.setEventSubcategory = ANALYTICS.LEFT_NAV[dataObject.setEventSubcategory];
				}
				else {
					const subCategory = dataObject.setEventSubcategory.split(':');
					if (subCategory.length === 2) {
						if (ANALYTICS.LEFT_NAV[subCategory[0]]) {
							dataObject.setEventSubcategory = `${ANALYTICS.LEFT_NAV[subCategory[0]]}:${subCategory[1]}`;
						}
						else if (ANALYTICS.LEFT_NAV[subCategory[1]]) {
							dataObject.setEventSubcategory = `${subCategory[0]}:${ANALYTICS.LEFT_NAV[subCategory[1]]}`;
						}
					}
				}
			}

			// Overiding content.category with its mapping
			if (dataObject.setContentCategory && ANALYTICS.PLATFORM[dataObject.setContentCategory]) {
				dataObject.setContentCategory = ANALYTICS.PLATFORM[dataObject.setContentCategory];
			}

			// overiding content.type
			if (dataObject.setContentType && componentParams && componentParams.source === ANALYTICS.APPS) {
				dataObject.setContentType = this.getContentType(dataObject.setContentType, sapCode);
			}

			// adding sophia analytics data in banner events
			if (componentParams && (componentParams.surfaceType === ANALYTICS.SURFACE_TYPE.ALL_APPS_BANNER || componentParams.surfaceType === ANALYTICS.SURFACE_TYPE.CATEGORY_BANNER)) {
				const analyticsObj = appsUtil.sophiaAnalyticsData[componentParams.surfaceType];
				if (analyticsObj) {
					dataObject.setExpResponseGuid = analyticsObj.data.analyticsData && analyticsObj.data.analyticsData.responseGUID;
					const {containers, surfaceAnalyticsData} = analyticsObj.data.surfaces && analyticsObj.data.surfaces[analyticsObj.surfaceId];
					if (containers) {
						const {containerAnalyticsData} = containers[0];
						dataObject.setExpContainerID = containerAnalyticsData.containerId;
						dataObject.setExpActionBlockId = containerAnalyticsData.actionBlockId;
						dataObject.setExpTreatmentID = containerAnalyticsData.treatmentId;
						setExpCampaignAndVariationFromSophia(dataObject, containerAnalyticsData.campaignId, containerAnalyticsData.variationId);
					}
					dataObject.setExpSurfaceID = surfaceAnalyticsData && surfaceAnalyticsData.surfaceId;
				}
			}

			// appending :64 or :32 to os version
			let osVersion = this.ingest.getOSVersion();
			if (appsUtil.isWin()) {
				osVersion += `:${DeviceDetectionUtil.is64bitWindows() ? '64' : '32'}`;
			}
			else if (appsUtil.isMacOS()) {
				osVersion += `:${DeviceDetectionUtil.is64bitMac() ? '64' : '32'}`;
			}
			this.ingest.setDataValue('source', 'os_version', osVersion);

			// in case of ffc-fallback
			if (appsUtil.isFFCFallback) {
				dataObject.setEventContextSequence = 'ffc-fallback';
			}

			dataObject.setIngestContextGuid = analyticsUtil.contextGuid;
			dataObject.setIngestContextParams = analyticsUtil.contextParams;
			dataObject.setIngestContextInit = analyticsUtil.contextInit;

			this.logAnalytics(dataObject);

			if (logAnalytics) {
				console.log('genericAnalyticEvent: ', dataObject);
			}
		});

		ingestUtil.sendToIngest(this.ingest, logMetricsCollection);
	}
}
